import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { Customer } from '../../types/bookkeeping.js';

const PAYMENT_TERMS = ['net0', 'net15', 'net30', 'net45', 'net60'] as const;

const CustomerSchema = z.object({
  display_name: z.string().min(1).max(200),
  legal_name: z.string().max(200).optional(),
  tax_id: z.string().max(40).optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(40).optional(),
  default_currency: z.string().length(3),
  default_payment_terms: z.enum(PAYMENT_TERMS).default('net30'),
  is_lead: z.boolean().default(false),
  notes: z.string().optional(),
});
type CustomerForm = z.infer<typeof CustomerSchema>;

function toFormDefaults(c?: Customer): CustomerForm {
  return {
    display_name: c?.display_name ?? '',
    legal_name: c?.legal_name ?? '',
    tax_id: c?.tax_id ?? '',
    email: c?.email ?? '',
    phone: c?.phone ?? '',
    default_currency: c?.default_currency ?? 'USD',
    default_payment_terms: c?.default_payment_terms ?? 'net30',
    is_lead: c?.is_lead ?? false,
    notes: c?.notes ?? '',
  };
}

function CustomerModal({
  customer,
  onClose,
  onSaved,
}: {
  customer?: Customer;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('bookkeeping');
  const isEdit = !!customer;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerForm>({
    resolver: zodResolver(CustomerSchema),
    defaultValues: toFormDefaults(customer),
  });

  const createMutation = useMutation({
    mutationFn: (input: CustomerForm) =>
      api('/bookkeeping/customers', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          legal_name: input.legal_name || null,
          tax_id: input.tax_id || null,
          email: input.email || null,
          phone: input.phone || null,
          notes: input.notes || null,
        }),
      }),
    onSuccess: onSaved,
  });

  const updateMutation = useMutation({
    mutationFn: (input: CustomerForm) =>
      api(`/bookkeeping/customers/${customer?.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...input,
          legal_name: input.legal_name || null,
          tax_id: input.tax_id || null,
          email: input.email || null,
          phone: input.phone || null,
          notes: input.notes || null,
        }),
      }),
    onSuccess: onSaved,
  });

  const onSubmit = handleSubmit((data) =>
    isEdit ? updateMutation.mutate(data) : createMutation.mutate(data),
  );

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? t('customers.editTitle') : t('customers.addButton')}
        </h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm">{t('customers.fields.displayName')} *</span>
            <input
              {...register('display_name')}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
            {errors.display_name && (
              <span className="text-xs text-red-600">{errors.display_name.message}</span>
            )}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('customers.fields.legalName')}</span>
              <input
                {...register('legal_name')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('customers.fields.taxId')}</span>
              <input
                {...register('tax_id')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('customers.fields.email')}</span>
              <input
                {...register('email')}
                type="email"
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
              {errors.email && <span className="text-xs text-red-600">{errors.email.message}</span>}
            </label>
            <label className="block">
              <span className="text-sm">{t('customers.fields.phone')}</span>
              <input
                {...register('phone')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('customers.fields.currency')} *</span>
              <input
                {...register('default_currency')}
                maxLength={3}
                className="mt-1 w-full rounded border px-3 py-2 text-sm uppercase"
              />
              {errors.default_currency && (
                <span className="text-xs text-red-600">{errors.default_currency.message}</span>
              )}
            </label>
            <label className="block">
              <span className="text-sm">{t('customers.fields.paymentTerms')}</span>
              <select
                {...register('default_payment_terms')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term} value={term}>
                    {term}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input {...register('is_lead')} type="checkbox" />
            {t('customers.fields.isLead')}
          </label>
          <label className="block">
            <span className="text-sm">{t('customers.fields.notes')}</span>
            <textarea
              {...register('notes')}
              rows={2}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {isEdit ? 'Guardar' : t('customers.addButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Customers() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Customer | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bookkeeping-customers', activeOrgId],
    queryFn: () => api<{ customers: Customer[] }>('/bookkeeping/customers'),
    enabled: !!activeOrgId,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookkeeping-customers'] }),
  });

  const handleSaved = () => {
    void qc.invalidateQueries({ queryKey: ['bookkeeping-customers'] });
    setAddOpen(false);
    setEditTarget(null);
  };

  const customers = data?.customers ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('customers.title')}</h1>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          {t('customers.addButton')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-hidden rounded border bg-white">
          <table className="w-full">
            <thead className="border-b text-left text-sm text-slate-600">
              <tr>
                <th className="p-3">{t('customers.fields.displayName')}</th>
                <th className="p-3">{t('customers.fields.email')}</th>
                <th className="p-3">{t('customers.fields.taxId')}</th>
                <th className="p-3">{t('customers.fields.currency')}</th>
                <th className="p-3">{t('customers.fields.isLead')}</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-sm text-slate-500">
                    No hay clientes aún.
                  </td>
                </tr>
              )}
              {customers.map((c) => (
                <tr key={c.id} className="border-b text-sm last:border-0">
                  <td className="p-3 font-medium">{c.display_name}</td>
                  <td className="p-3 text-slate-600">{c.email ?? '—'}</td>
                  <td className="p-3 text-slate-600">{c.tax_id ?? '—'}</td>
                  <td className="p-3">{c.default_currency}</td>
                  <td className="p-3">
                    {c.is_lead && (
                      <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                        Lead
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditTarget(c)}
                      className="mr-2 text-xs text-blue-600 hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => deactivate.mutate(c.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Desactivar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && <CustomerModal onClose={() => setAddOpen(false)} onSaved={handleSaved} />}
      {editTarget && (
        <CustomerModal
          customer={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
